import { useState } from 'react';

export interface DistributionChartDatum {
  id: string;
  label: string;
  value: number;
  color?: string | undefined;
}

interface DistributionChartProps {
  ariaLabel: string;
  data: DistributionChartDatum[];
  valueFormatter?: ((value: number) => string) | undefined;
  emptyMessage: string;
}

const colors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
];

export function ResponsiveBarChart(props: DistributionChartProps) {
  const [active, setActive] = useState<string | null>(null);
  if (props.data.length === 0) return <Empty message={props.emptyMessage} />;
  const max = Math.max(...props.data.map(({ value }) => value), 1);
  return (
    <div className="min-w-0 space-y-3" role="group" aria-label={props.ariaLabel}>
      {props.data.map((datum, index) => {
        const color = datum.color ?? colors[index % colors.length];
        return (
          <button
            key={datum.id}
            type="button"
            className="grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_4rem] items-center gap-3 text-left text-xs focus-visible:outline-none"
            onFocus={() => {
              setActive(datum.id);
            }}
            onBlur={() => {
              setActive(null);
            }}
            onPointerEnter={() => {
              setActive(datum.id);
            }}
            onPointerLeave={() => {
              setActive(null);
            }}
            aria-label={`${datum.label}: ${format(props, datum.value)}`}
          >
            <span className="min-w-0">
              <span className="text-muted-foreground block truncate">{datum.label}</span>
              <span className="bg-muted mt-1 block h-2 overflow-hidden rounded-sm">
                <span
                  className="block h-full rounded-sm transition-[width]"
                  style={{
                    width: `${String(Math.max(2, (datum.value / max) * 100))}%`,
                    background: color,
                  }}
                />
              </span>
            </span>
            <span
              className={
                active === datum.id ? 'text-foreground font-semibold' : 'text-muted-foreground'
              }
            >
              {format(props, datum.value)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function ResponsiveDonutChart(props: DistributionChartProps) {
  const [active, setActive] = useState<string | null>(null);
  if (props.data.length === 0) return <Empty message={props.emptyMessage} />;
  const total = props.data.reduce((sum, datum) => sum + Math.max(0, datum.value), 0);
  const segments = props.data.map((datum, index) => {
    const contribution = Math.max(0, datum.value);
    const percentage = total > 0 ? (contribution / total) * 100 : 0;
    const priorContribution = props.data
      .slice(0, index)
      .reduce((sum, priorDatum) => sum + Math.max(0, priorDatum.value), 0);
    const priorPercentage = total > 0 ? (priorContribution / total) * 100 : 0;
    return {
      datum,
      percentage,
      dashOffset: 25 - priorPercentage,
      color: datum.color ?? colors[index % colors.length],
    };
  });
  return (
    <div className="grid min-w-0 items-center gap-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
      <svg
        viewBox="0 0 120 120"
        className="mx-auto h-40 w-40"
        role="group"
        aria-label={props.ariaLabel}
      >
        <circle cx="60" cy="60" r="42" fill="none" stroke="var(--muted)" strokeWidth="18" />
        {segments.map(({ datum, percentage, dashOffset, color }) => {
          return (
            <circle
              key={datum.id}
              cx="60"
              cy="60"
              r="42"
              fill="none"
              stroke={color}
              strokeWidth={active === datum.id ? 21 : 18}
              pathLength="100"
              strokeDasharray={`${String(percentage)} ${String(100 - percentage)}`}
              strokeDashoffset={dashOffset}
              className="cursor-pointer transition-[stroke-width]"
              tabIndex={0}
              onFocus={() => {
                setActive(datum.id);
              }}
              onBlur={() => {
                setActive(null);
              }}
              onPointerEnter={() => {
                setActive(datum.id);
              }}
              onPointerLeave={() => {
                setActive(null);
              }}
              aria-label={`${datum.label}: ${format(props, datum.value)}`}
            >
              <title>{`${datum.label}: ${format(props, datum.value)}`}</title>
            </circle>
          );
        })}
      </svg>
      <ul className="min-w-0 space-y-2 text-xs">
        {props.data.map((datum, index) => (
          <li key={datum.id} className="flex min-w-0 items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: datum.color ?? colors[index % colors.length] }}
              />
              <span className="truncate">{datum.label}</span>
            </span>
            <span className="text-muted-foreground">{format(props, datum.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const defaultValueFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

function format(props: DistributionChartProps, value: number) {
  return props.valueFormatter?.(value) ?? defaultValueFormat.format(value);
}

function Empty({ message }: { message: string }) {
  return (
    <div className="text-muted-foreground flex h-40 items-center justify-center text-sm">
      {message}
    </div>
  );
}
